import { all, put, takeLatest, call, delay, select } from 'redux-saga/effects';
import { BelActionTypes, BelEnquiryDetails, BelState } from './types';
import {
  uploadFileSuccess,
  uploadFileFailure,
  transformFileStart,
  transformFileSuccess,
  transformFileFailure,
  pollJobStart,
  pollJobUpdate,
  pollJobFailure,
  pollJobComplete,
  pollJobTimeout,
  fetchDownloadSuccess,
  fetchBelSuccess,
  submitBelDetailsSuccess,
  submitBelDetailsFailure,
  validateGeometrySettingsStart,
  validateGeometrySettingsSuccess,
  validateGeometrySettingsFailure,
  applyGeometryPipelineStart,
  applyGeometryPipelineSuccess,
  applyGeometryPipelineFailure,
  setBelStep,
  setValidationSummary,
  createBelCheckoutStart,
  createBelCheckoutSuccess,
  createBelCheckoutFailure,
  fetchLivePriceSuccess,
  fetchLivePriceFailure,
  verifyBelPaymentSuccess
} from './actions';
import {
  requestPresignedUpload,
  uploadFileToS3,
  transformFileToBulk,
  getJobStatus,
  getBulkEnquiryLodgement,
  updateBulkEnquiryLodgement,
  validateBulkGeometrySettings,
  getSystemFileDownloadUrl,
  JobStatusResponse,
  UpdateBulkLodgementBody,
  BulkEnquiryLodgementResponse
} from 'services/bulkEnquiryLodgement.service';
import {
  createBelCheckoutSession,
  getCheckoutSessionStatus,
  getPaymentTypePrice
} from 'services/payment.service';
import {
  mapPaymentTypePriceToStripePrice,
  PaymentTypePriceResponse
} from 'modules/bulkEnquiryLodgement/utils/formatBelPrice';
import {
  formatBelFileValidationFailedMessage,
  formatBelNoValidShapesMessage,
  formatUserFacingErrorCode,
  BEL_SUPPORT_EMAIL
} from 'modules/bulkEnquiryLodgement/constants/errorMessages';
import {
  getBelGeometrySettings,
  getEffectiveGeometryValues,
  buildValidateGeometrySettingsBody,
  formatGeometryViolationMessages,
  isBufferDistanceValid,
  isMaxSplitAreaValid,
  parseGeometryValidationPayload
} from 'modules/bulkEnquiryLodgement/geometrySettings.utils';

const BEL_SAGA_LOG = '[BEL saga]';

function logBelSaga(context: string, err: any): void {
  console.error(`${BEL_SAGA_LOG} ${context}`, err); // eslint-disable-line no-console
}

const getBelState = (state: { belCache: BelState }) => state.belCache;

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const POLL_INTERVAL_MS = 3000;
const MAX_BACKOFF_MS = 30_000;
const MAX_POLL_DURATION_MS = 10 * 60 * 1000;

const ACCEPTED_EXTENSIONS = /\.(kmz|kml|geojson|zip|gpkg)$/i;

type BelLocationTypeApi = 'Road Reserve' | 'Private';
type BelLocationInRoadApi = 'Road' | 'Nature Strip' | 'Footpath';

type BelEnquiryDetailsApiExtras = {
  bufferDistance?: number;
  maxSplitAreaSqm?: number;
};

const LOCATION_TYPE_MAP: { [key: string]: BelLocationTypeApi } = {
  footpath: 'Road Reserve',
  'nature strip': 'Road Reserve',
  road: 'Road Reserve',
  private: 'Private',
  'private property': 'Private'
};

const LOCATION_IN_ROAD_MAP: { [key: string]: BelLocationInRoadApi } = {
  footpath: 'Footpath',
  'nature strip': 'Nature Strip',
  road: 'Road'
};

function mapBelEnquiryDetailsToApi(
  details: BelEnquiryDetails,
  extras?: BelEnquiryDetailsApiExtras
): UpdateBulkLodgementBody {
  const locationTypes = [
    ...new Set(
      details.locationTypes
        .map((lt) => LOCATION_TYPE_MAP[lt.toLowerCase()])
        .filter((v) => v != null)
    )
  ];

  const locationsInRoad = details.locationTypes
    .map((lt) => LOCATION_IN_ROAD_MAP[lt.toLowerCase()])
    .filter((v) => v != null);

  const trimmedRef = details.userReference.trim();

  const activityTypesPayload =
    details.activityTypes.length > 0
      ? details.activityTypes
      : details.activity
      ? [details.activity]
      : undefined;

  const body: UpdateBulkLodgementBody = {
    digStartAt: details.digStartAt || undefined,
    digEndAt: details.digEndAt || undefined,
    isPlanningJob:
      details.activityType === 'design' || details.activityType === 'planning',
    activityTypes: activityTypesPayload,
    locationTypes: locationTypes.length > 0 ? locationTypes : undefined,
    locationsInRoad,
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    source: 'WEB'
  };

  if (details.useShapeNamesFromFile && details.userReferenceProperty) {
    body.userReferenceField = details.userReferenceProperty;
  } else {
    // Explicitly clear any previously persisted file-attribute mapping
    // (Back from invoice → uncheck "use attributes from my file").
    body.userReferenceField = null;
    if (trimmedRef) {
      body.userReference = trimmedRef;
    } else {
      body.userReference = null;
    }
  }

  if (details.notes?.trim()) {
    body.notes = details.notes.trim();
  } else {
    // Explicitly clear previously persisted notes (Back from invoice → empty notes).
    body.notes = null;
  }

  body.autoRenewUntil =
    details.autoRenew && details.autoRenewUntil ? details.autoRenewUntil : null;

  if (extras?.bufferDistance != null) {
    body.bufferDistance = extras.bufferDistance;
  }

  if (extras?.maxSplitAreaSqm != null) {
    body.maxSplitAreaSqm = extras.maxSplitAreaSqm;
  }

  return body;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function validateFileForUpload(file: File): string | null {
  if (file.size > MAX_UPLOAD_BYTES) {
    return 'File is too large. Maximum size is 10 MB.';
  }
  if (!ACCEPTED_EXTENSIONS.test(file.name)) {
    return 'Unsupported file type. Use KMZ, KML, GeoJSON, GeoPackage (GPKG), or Shapefile (ZIP).';
  }
  return null;
}

function* doUploadFile(action: { type: string; payload: { file: File } }) {
  const { file } = action.payload;
  const validationError = validateFileForUpload(file);
  if (validationError) {
    yield put(uploadFileFailure(validationError));
    return;
  }
  try {
    const uploadMeta = yield call(requestPresignedUpload, {
      name: file.name,
      mimeType: file.type || undefined
    });
    const presignedUrl = uploadMeta.url;
    const fileId = uploadMeta.id;

    const s3Response: Response = yield call(uploadFileToS3, presignedUrl, file);
    if (!s3Response.ok) {
      yield put(
        uploadFileFailure(
          'File upload failed. Please check your connection and try again.'
        )
      );
      return;
    }

    yield put(
      uploadFileSuccess({
        fileId,
        fileName: file.name,
        fileSize: formatFileSize(file.size)
      })
    );
    yield put(transformFileStart(fileId));
  } catch (err) {
    logBelSaga('doUploadFile', err);
    yield put(
      uploadFileFailure(
        'File upload failed. Please check your connection and try again.'
      )
    );
  }
}

function* watchUploadFile() {
  yield takeLatest(BelActionTypes.UPLOAD_FILE_START, doUploadFile);
}

function* doTransformFile(action: { type: string; payload: number }) {
  const fileId = action.payload;
  try {
    const result = yield call(transformFileToBulk, fileId);
    yield put(
      transformFileSuccess({
        bulkId: result.bulkId,
        jobId: result.Job.id
      })
    );
    yield put(pollJobStart({ jobId: result.Job.id, pollMode: 'transform' }));
  } catch (err) {
    logBelSaga('doTransformFile', err);
    const errObj: any = err;
    const status = errObj?.status;
    let message = `Could not start file processing. Please try again or contact ${BEL_SUPPORT_EMAIL}.`;
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    }
    yield put(transformFileFailure(message));
  }
}

function* watchTransformFile() {
  yield takeLatest(BelActionTypes.TRANSFORM_FILE_START, doTransformFile);
}

function* waitForBelValidation(
  jobId: number,
  bulkId: number,
  alreadyElapsedMs: number
) {
  const BEL_VALID_STATUSES = ['UPLOADED', 'UPDATED', 'UPDATING', 'COMPLETED'];
  let elapsedMs = alreadyElapsedMs;

  while (elapsedMs < MAX_POLL_DURATION_MS) {
    try {
      const bel = yield call(getBulkEnquiryLodgement, bulkId);

      if (bel.status === 'FAILED') {
        yield put(
          pollJobFailure(
            formatBelFileValidationFailedMessage(
              undefined,
              ` (Bulk ID: ${bulkId})`
            )
          )
        );
        return;
      }

      if (BEL_VALID_STATUSES.includes(bel.status)) {
        const updatedJob: JobStatusResponse = yield call(getJobStatus, jobId);
        const jobResponse: any = updatedJob.response;
        const summary = jobResponse?.summary;

        if (summary) {
          if ((summary.totalFeatures ?? 0) === 0) {
            yield put(
              pollJobFailure(
                formatBelNoValidShapesMessage(` (Bulk ID: ${bulkId})`)
              )
            );
            return;
          }

          yield put(fetchBelSuccess(bel));
          yield put(
            setValidationSummary({
              hasNonPointGeometry: summary.hasNonPointGeometry === true,
              geometryTypes: summary.geometryTypes ?? [],
              totalFeatures: summary.totalFeatures ?? 0,
              totalFeaturesInFile:
                summary.totalFeaturesInFile ?? summary.totalFeatures ?? 0,
              totalAreaSqm: summary.totalAreaSqm ?? 0,
              maxLineLengthM: summary.maxLineLengthM ?? 0,
              hasPolygonExceeding20Km2: summary.hasPolygonExceeding20Km2,
              skippedUnderMinArea: summary.skippedUnderMinArea ?? 0,
              fields: summary.fields ?? []
            })
          );
          yield put(
            validateGeometrySettingsStart({ pendingStepAdvance: false })
          );
          return;
        } else if ((bel.totalBulkEnquiries ?? 0) === 0) {
          yield put(
            pollJobFailure(
              formatBelNoValidShapesMessage(` (Bulk ID: ${bulkId})`)
            )
          );
          return;
        }

        yield put(fetchBelSuccess(bel));
        return;
      }
    } catch (err) {
      logBelSaga(
        `waitForBelValidation getBulkEnquiryLodgement (bulkId=${bulkId}, jobId=${jobId})`,
        err
      );
    }

    yield delay(POLL_INTERVAL_MS);
    elapsedMs += POLL_INTERVAL_MS;
  }

  yield put(
    pollJobFailure(
      `Processing failed We're unable to process your file; please try again and contact support if the issue persists.`
    )
  );
}

function* pollTransformJob(jobId: number, bulkId: number) {
  let consecutiveFailures = 0;
  let elapsedMs = 0;
  let intervalMs = POLL_INTERVAL_MS;

  while (true) {
    try {
      const job: JobStatusResponse = yield call(getJobStatus, jobId);
      consecutiveFailures = 0;
      intervalMs = POLL_INTERVAL_MS;

      yield put(pollJobUpdate(job.status));

      if (job.status === 'FAILED') {
        const jobResponse: any = job.response;
        const jobResponseError = jobResponse?.error;
        yield put(
          pollJobFailure(
            formatBelFileValidationFailedMessage(
              jobResponseError,
              formatUserFacingErrorCode(jobId)
            )
          )
        );
        return;
      }

      if (job.status === 'COMPLETED') {
        const reqObj: any = job.request;
        const reqType = reqObj?.type;
        if (
          job.type === 'PROCESS_FILES' &&
          reqType === 'TRANSFORM_FILE_TO_BULK'
        ) {
          // The sqsSystemJobUpdater marks the job COMPLETED when the GP job
          // is *submitted*, not when it finishes. Poll the BEL until the GP
          // updates it to UPLOADED, then re-fetch the job for the summary.
          yield* waitForBelValidation(jobId, bulkId, elapsedMs);
        } else {
          yield put(
            pollJobFailure(
              `Unexpected job result.${formatUserFacingErrorCode(jobId)}`
            )
          );
        }
        return;
      }
    } catch (err) {
      logBelSaga(`pollTransformJob getJobStatus (jobId=${jobId})`, err);
      consecutiveFailures++;
      intervalMs = Math.min(intervalMs * 2, MAX_BACKOFF_MS);
      if (consecutiveFailures >= 3) {
        yield put(pollJobUpdate('PROCESSING'));
      }
    }

    yield delay(intervalMs);
    elapsedMs += intervalMs;

    if (elapsedMs >= MAX_POLL_DURATION_MS) {
      yield put(
        pollJobFailure(
          `TIMEOUT:This is taking longer than expected. You can wait or check back later, or contact ${BEL_SUPPORT_EMAIL}.${formatUserFacingErrorCode(
            jobId
          )}`
        )
      );
      return;
    }
  }
}

function* tryFetchBelDownloadUrl(fileId: number) {
  try {
    const { url } = yield call(getSystemFileDownloadUrl, fileId);
    yield put(fetchDownloadSuccess({ url }));
    return true;
  } catch (err) {
    logBelSaga(
      `getSystemFileDownloadUrl after bulk COMPLETED (fileId=${fileId})`,
      err
    );
    return false;
  }
}

function* finalizeBulkLodgementPoll(bel: BulkEnquiryLodgementResponse) {
  yield put(
    pollJobComplete({
      enquiriesLodged: bel.totalEnquiriesCreated ?? bel.totalBulkEnquiries ?? 0
    })
  );
  yield put(setBelStep(3));
}

function* pollBulkLodgementUntilDone(bulkId: number) {
  let elapsedMs = 0;
  let intervalMs = POLL_INTERVAL_MS;
  let lodgementCountsApplied = false;
  let lastBel: BulkEnquiryLodgementResponse | null = null;

  while (true) {
    try {
      const bel: BulkEnquiryLodgementResponse = yield call(
        getBulkEnquiryLodgement,
        bulkId,
        [
          'BufferJob',
          'SplitJob',
          'UpdateFilesJob',
          'ConvertJob',
          'ProcessFilesJob'
        ]
      );
      lastBel = bel;
      intervalMs = POLL_INTERVAL_MS;

      yield put(fetchBelSuccess(bel));

      const failedPipelineJob = [
        bel.BufferJob,
        bel.SplitJob,
        bel.ConvertJob,
        bel.UpdateFilesJob
      ].find((job) => job?.status === 'FAILED');

      if (bel.status === 'FAILED' || failedPipelineJob) {
        yield put(
          pollJobFailure(
            `Bulk processing failed. Please contact ${BEL_SUPPORT_EMAIL}. (Bulk ID: ${bulkId})`
          )
        );
        return;
      }

      if (bel.status === 'COMPLETED') {
        const exportJob = bel.UpdateFilesJob;
        const exportFailed = exportJob?.status === 'FAILED';
        const exportPending =
          exportJob &&
          exportJob.status !== 'COMPLETED' &&
          exportJob.status !== 'FAILED';
        const fileId = bel.updatedFileId ?? bel.UpdatedFile?.id;
        const needsDownload = Boolean(exportJob || fileId);

        if (!exportPending) {
          const belState: BelState = yield select(getBelState);
          let hasDownloadUrl = Boolean(belState.downloadUrl);

          if (fileId && !hasDownloadUrl) {
            hasDownloadUrl = yield call(tryFetchBelDownloadUrl, fileId);
          }

          const readyToFinalize =
            !needsDownload || hasDownloadUrl || exportFailed;

          if (readyToFinalize) {
            if (!lodgementCountsApplied) {
              yield call(finalizeBulkLodgementPoll, bel);
              lodgementCountsApplied = true;
            }
            return;
          }
        }
      }
    } catch (err) {
      logBelSaga(
        `pollBulkLodgementUntilDone getBulkEnquiryLodgement (bulkId=${bulkId})`,
        err
      );
      intervalMs = Math.min(intervalMs * 2, MAX_BACKOFF_MS);
    }

    yield delay(intervalMs);
    elapsedMs += intervalMs;

    if (elapsedMs >= MAX_POLL_DURATION_MS) {
      if (lastBel?.status === 'COMPLETED') {
        const fileId = lastBel.updatedFileId ?? lastBel.UpdatedFile?.id ?? null;
        const belState: BelState = yield select(getBelState);
        if (fileId && !belState.downloadUrl) {
          yield call(tryFetchBelDownloadUrl, fileId);
        }
        if (!lodgementCountsApplied) {
          yield call(finalizeBulkLodgementPoll, lastBel);
        }
        return;
      }
      yield put(
        pollJobTimeout(
          `TIMEOUT:Bulk processing is taking longer than expected. (Bulk ID: ${bulkId})`
        )
      );
      return;
    }
  }
}

function* doPollJob(action: {
  type: string;
  payload: { jobId: number; pollMode: 'transform' | 'bulkStatus' | null };
}) {
  const { jobId, pollMode } = action.payload;
  if (pollMode === 'transform') {
    const state: BelState = yield select(getBelState);
    const bulkId = state.belId;
    if (!bulkId) {
      yield put(pollJobFailure('Missing bulk ID after transform.'));
      return;
    }
    yield call(pollTransformJob, jobId, bulkId);
  }
}

function* watchPollJob() {
  yield takeLatest(BelActionTypes.POLL_JOB_START, doPollJob);
}

function isBulkGeometryPipelineReady(
  bel: BulkEnquiryLodgementResponse
): boolean {
  if (bel.status === 'FAILED') {
    return false;
  }
  if (bel.BufferJob?.status === 'FAILED' || bel.SplitJob?.status === 'FAILED') {
    return false;
  }
  if (bel.BufferJob && bel.BufferJob.status !== 'COMPLETED') {
    return false;
  }
  if (bel.SplitJob && bel.SplitJob.status !== 'COMPLETED') {
    return false;
  }
  return bel.status === 'UPDATED';
}

function formatBelCheckoutError(err: any): string {
  const status = err?.status;
  const message =
    err?.response?.error?.message ??
    err?.response?.error?.error?.message ??
    err?.message;

  if (status === 409 && message === 'BULK_GEOMETRY_PIPELINE_NOT_READY') {
    return 'Your file is still being prepared. Please wait a moment and try again.';
  }
  if (status === 0) {
    return 'Unable to reach the server. Please check your connection and try again.';
  }
  return 'Could not create payment session. Please try again.';
}

function buildGeometryUpdateBodyFromState(
  state: BelState
): UpdateBulkLodgementBody {
  const geometrySettings = getBelGeometrySettings(
    state.validationSummary?.geometryTypes ?? [],
    state.validationSummary?.hasPolygonExceeding20Km2
  );
  const effective = getEffectiveGeometryValues(
    geometrySettings,
    state.applyBuffer,
    state.applySplit,
    state.bufferDistance,
    state.maxSplitAreaKm
  );
  const body: UpdateBulkLodgementBody = {
    bufferDistance: effective.bufferDistance
  };
  if (effective.maxSplitAreaSqm !== null) {
    body.maxSplitAreaSqm = effective.maxSplitAreaSqm;
  }
  return body;
}

function* waitForBulkGeometryPipeline(bulkId: number) {
  let elapsedMs = 0;
  let intervalMs = POLL_INTERVAL_MS;

  while (elapsedMs < MAX_POLL_DURATION_MS) {
    try {
      const bel: BulkEnquiryLodgementResponse = yield call(
        getBulkEnquiryLodgement,
        bulkId,
        ['BufferJob', 'SplitJob']
      );

      if (bel.status === 'FAILED') {
        yield put(
          applyGeometryPipelineFailure(
            `Geometry preparation failed. Please contact ${BEL_SUPPORT_EMAIL}. (Bulk ID: ${bulkId})`
          )
        );
        return false;
      }

      if (
        bel.BufferJob?.status === 'FAILED' ||
        bel.SplitJob?.status === 'FAILED'
      ) {
        yield put(
          applyGeometryPipelineFailure(
            `Geometry preparation failed. Please contact ${BEL_SUPPORT_EMAIL}. (Bulk ID: ${bulkId})`
          )
        );
        return false;
      }

      if (isBulkGeometryPipelineReady(bel)) {
        yield put(fetchBelSuccess(bel));
        return true;
      }

      intervalMs = POLL_INTERVAL_MS;
    } catch (err) {
      logBelSaga(`waitForBulkGeometryPipeline (bulkId=${bulkId})`, err);
      intervalMs = Math.min(intervalMs * 2, MAX_BACKOFF_MS);
    }

    yield delay(intervalMs);
    elapsedMs += intervalMs;
  }

  yield put(
    applyGeometryPipelineFailure(
      `Geometry preparation is taking longer than expected. Please try again or contact ${BEL_SUPPORT_EMAIL}. (Bulk ID: ${bulkId})`
    )
  );
  return false;
}

function* applyGeometryPipelineAndAdvance(bulkId: number) {
  const state: BelState = yield select(getBelState);
  yield put(applyGeometryPipelineStart());

  try {
    const body = buildGeometryUpdateBodyFromState(state);
    const updateResponse: { Jobs?: { id: number }[] } = yield call(
      updateBulkEnquiryLodgement,
      bulkId,
      body
    );

    if ((updateResponse.Jobs?.length ?? 0) > 0) {
      const pipelineReady: boolean = yield call(
        waitForBulkGeometryPipeline,
        bulkId
      );
      if (!pipelineReady) {
        return;
      }
    }

    yield put(applyGeometryPipelineSuccess());
    yield put(setBelStep(2));
  } catch (err) {
    logBelSaga('applyGeometryPipelineAndAdvance', err);
    const errObj: any = err;
    const status = errObj?.status;
    let message = 'Could not process your file. Please try again.';
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    } else if (status === 400) {
      const parsed = parseGeometryValidationPayload(
        errObj?.response?.error?.details ??
          errObj?.response?.error ??
          errObj?.response ??
          errObj
      );
      message =
        (parsed && formatGeometryViolationMessages(parsed.violations)) ||
        errObj?.message ||
        'Some geometry settings are invalid. Please check your input.';
    } else if (status === 401) {
      message = 'Your session has expired. Please log in again.';
    }
    yield put(applyGeometryPipelineFailure(message));
  }
}

function* advanceToDetailsAfterUploadValidation() {
  const state: BelState = yield select(getBelState);
  const bulkId = state.belId;
  if (!bulkId) {
    yield put(
      applyGeometryPipelineFailure(
        'Missing large area enquiry ID after validation.'
      )
    );
    return;
  }

  if (state.geometryPipelineApplied) {
    yield put(setBelStep(2));
    return;
  }

  yield call(applyGeometryPipelineAndAdvance, bulkId);
}

function belGeometrySettingsAreLocallyValid(state: BelState): boolean {
  if (!state.validationSummary) {
    return false;
  }

  const geometrySettings = getBelGeometrySettings(
    state.validationSummary.geometryTypes ?? [],
    state.validationSummary.hasPolygonExceeding20Km2
  );

  const bufferActive = geometrySettings.bufferRequired || state.applyBuffer;
  const splitActive =
    geometrySettings.showSplit &&
    (geometrySettings.splitRequired || state.applySplit);

  if (
    bufferActive &&
    !isBufferDistanceValid(state.bufferDistance, geometrySettings)
  ) {
    return false;
  }

  if (
    splitActive &&
    !isMaxSplitAreaValid(state.maxSplitAreaKm, geometrySettings)
  ) {
    return false;
  }

  return true;
}

function* doValidateGeometrySettings(action: {
  type: string;
  payload?: { pendingStepAdvance?: boolean };
}) {
  const state: BelState = yield select(getBelState);
  const bulkId = state.belId;

  if (!bulkId || !state.validationSummary || !state.fileInfo?.isValid) {
    return;
  }

  if (!belGeometrySettingsAreLocallyValid(state)) {
    yield put(
      validateGeometrySettingsSuccess({
        canProceed: false,
        requiresSplit: false,
        violations: []
      })
    );
    return;
  }

  const geometrySettings = getBelGeometrySettings(
    state.validationSummary.geometryTypes ?? [],
    state.validationSummary.hasPolygonExceeding20Km2
  );

  const body = buildValidateGeometrySettingsBody(
    geometrySettings,
    state.applyBuffer,
    state.applySplit,
    state.bufferDistance,
    state.maxSplitAreaKm
  );

  try {
    const response = yield call(validateBulkGeometrySettings, bulkId, body);
    yield put(
      validateGeometrySettingsSuccess({
        canProceed: response.canProceed === true,
        requiresSplit: response.requiresSplit === true,
        violations: response.violations ?? []
      })
    );

    if (action.payload?.pendingStepAdvance && response.canProceed === true) {
      yield call(advanceToDetailsAfterUploadValidation);
    }
  } catch (err) {
    logBelSaga('doValidateGeometrySettings', err);
    const errObj: any = err;
    const parsed = parseGeometryValidationPayload(
      errObj?.response?.error?.details ??
        errObj?.response?.error ??
        errObj?.response ??
        errObj
    );
    if (parsed) {
      yield put(
        validateGeometrySettingsSuccess({
          canProceed: parsed.canProceed,
          requiresSplit: parsed.requiresSplit,
          violations: parsed.violations
        })
      );
      if (action.payload?.pendingStepAdvance && parsed.canProceed) {
        yield call(advanceToDetailsAfterUploadValidation);
      }
      return;
    }

    const status = errObj?.status;
    let message = 'Could not validate geometry settings. Please try again.';
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    } else if (status === 409) {
      message = 'File processing is not complete. Please wait and try again.';
    } else if (errObj?.message) {
      message = errObj.message;
    }
    yield put(validateGeometrySettingsFailure(message));
  }
}

function* watchValidateGeometrySettings() {
  yield takeLatest(
    BelActionTypes.VALIDATE_GEOMETRY_SETTINGS_START,
    doValidateGeometrySettings
  );
}

function* doSubmitBelDetails() {
  const state: BelState = yield select(getBelState);
  const bulkId = state.belId;
  if (!bulkId) {
    yield put(
      submitBelDetailsFailure('No large area enquiry ID. Upload a file first.')
    );
    return;
  }
  try {
    const body = mapBelEnquiryDetailsToApi(state.enquiryDetails);
    yield call(updateBulkEnquiryLodgement, bulkId, body);

    yield put(submitBelDetailsSuccess());

    try {
      sessionStorage.setItem('bel_bulkId', String(bulkId));
      yield put(createBelCheckoutStart());
      const checkoutResponse: any = yield call(createBelCheckoutSession, {
        paymentType: 'BULK_ENQUIRY_LODGEMENT',
        returnUrl: `${window.location.origin}${window.location.pathname}?payment=complete&session_id={CHECKOUT_SESSION_ID}`,
        quantity: 1,
        metaData: { bulkId: String(bulkId) }
      });
      yield put(
        createBelCheckoutSuccess({
          clientSecret: checkoutResponse.clientSecret,
          invoiceId: checkoutResponse.postPaymentId
            ? String(checkoutResponse.postPaymentId)
            : undefined,
          completed: checkoutResponse.completed
        })
      );
    } catch (checkoutErr) {
      logBelSaga('doSubmitBelDetails createCheckout', checkoutErr);
      yield put(createBelCheckoutFailure(formatBelCheckoutError(checkoutErr)));
    }

    yield put(setBelStep(3));
  } catch (err) {
    logBelSaga('doSubmitBelDetails', err);
    const errObj: any = err;
    const status = errObj?.status;
    let message = 'Could not submit enquiry details. Please try again.';
    if (status === 0) {
      message =
        'Unable to reach the server. Please check your connection and try again.';
    } else if (status === 400) {
      const parsed = parseGeometryValidationPayload(
        errObj?.response?.error?.details ??
          errObj?.response?.error ??
          errObj?.response ??
          errObj
      );
      message =
        (parsed && formatGeometryViolationMessages(parsed.violations)) ||
        errObj?.message ||
        'Some fields are invalid. Please check your input.';
    } else if (status === 401) {
      message = 'Your session has expired. Please log in again.';
    }
    yield put(submitBelDetailsFailure(message));
  }
}

function* doConfirmBelPayment() {
  const state: BelState = yield select(getBelState);
  const bulkId = state.belId;
  if (!bulkId) {
    yield put(pollJobFailure('No large area enquiry ID found.'));
    return;
  }
  yield call(pollBulkLodgementUntilDone, bulkId);
}

function* doVerifyBelPayment(action: {
  type: string;
  payload: { sessionId: string };
}) {
  const { sessionId } = action.payload;
  const bulkId = sessionStorage.getItem('bel_bulkId');

  if (!bulkId) {
    yield put(
      pollJobFailure('No large area enquiry ID found after payment redirect.')
    );
    return;
  }

  try {
    const statusResponse: any = yield call(getCheckoutSessionStatus, sessionId);
    if (statusResponse.status !== 'complete') {
      yield put(pollJobFailure('Payment was not completed. Please try again.'));
      return;
    }
    yield put(
      verifyBelPaymentSuccess({
        paymentIntentId: statusResponse.paymentIntentId ?? null
      })
    );
    sessionStorage.removeItem('bel_bulkId');
    yield put(setBelStep(3));
    yield call(pollBulkLodgementUntilDone, Number(bulkId));
  } catch (err) {
    logBelSaga('doVerifyBelPayment', err);
    yield put(
      pollJobFailure('Failed to verify payment status. Please try again.')
    );
  }
}

function* watchSubmitBelDetails() {
  yield takeLatest(BelActionTypes.SUBMIT_BEL_DETAILS_START, doSubmitBelDetails);
}

function* watchConfirmBelPayment() {
  yield takeLatest(BelActionTypes.CONFIRM_BEL_PAYMENT, doConfirmBelPayment);
}

function* watchVerifyBelPayment() {
  yield takeLatest(BelActionTypes.VERIFY_BEL_PAYMENT, doVerifyBelPayment);
}

function* doFetchLivePrice() {
  try {
    const response: PaymentTypePriceResponse = yield call(
      getPaymentTypePrice,
      'BULK_ENQUIRY_LODGEMENT'
    );
    yield put(
      fetchLivePriceSuccess(mapPaymentTypePriceToStripePrice(response))
    );
  } catch {
    yield put(fetchLivePriceFailure('Unable to fetch price.'));
  }
}

function* watchFetchLivePrice() {
  yield takeLatest(BelActionTypes.FETCH_LIVE_PRICE_START, doFetchLivePrice);
}

export default function* belSagas() {
  yield all([
    call(watchUploadFile),
    call(watchTransformFile),
    call(watchPollJob),
    call(watchValidateGeometrySettings),
    call(watchSubmitBelDetails),
    call(watchConfirmBelPayment),
    call(watchVerifyBelPayment),
    call(watchFetchLivePrice)
  ]);
}
